import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageLayout } from './layout'
import {
  fetchProducts,
  fetchProductTypes,
  fetchVolumeDiscounts,
  fetchPriceTiers,
  fetchProductPrices,
  createProduct,
  updateProduct,
  deleteProduct,
  createProductType,
  updateProductType,
  deleteProductType,
  createVolumeDiscount,
  updateVolumeDiscount,
  deleteVolumeDiscount
} from '../supabase-client'
import { useDataCache } from '../contexts/DataCacheContext'
import { supabase } from '@/supabase-client'
import PricingSettings from './PricingSettings'
import PriceCatalogue from './PriceCatalogue'
import WholesaleThresholds from './WholesaleThresholds'
import BlockFormatsTab from './BlockFormatsTab'
import BlockPriceCatalogue from './BlockPriceCatalogue'
import GrowingMaterialsTab from './GrowingMaterialsTab'
import GrowingMaterialPriceCatalogue from './GrowingMaterialPriceCatalogue'
import PromotionsTab from './PromotionsTab'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DecimalInput } from '@/components/ui/decimal-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DashboardTabs } from '@/components/ui/dashboard-tabs'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { OdinButton } from '@/components/ui/odin-button'
import { EditIcon } from '@/components/ui/icons/EditIcon'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import ToggleSwitch from './ToggleSwitch'
import StrainFamilyComboBox from './StrainFamilyComboBox'
import { Package, Settings, Percent, Coins, BookOpen, Scale, Layers, Box, Tag, Sprout } from 'lucide-react'

// Safe parseFloat to prevent NaN and TDZ issues
const safeParseFloat = (value) => {
  const parsed = parseFloat(value)
  return isNaN(parsed) ? 0 : parsed
}

function ProductDashboard({ user, onLogout, defaultTab }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { invalidateCache } = useDataCache()
  
  // Initialize all state variables using lazy initialization
  const [products, setProducts] = useState(() => [])
  const [productTypes, setProductTypes] = useState(() => [])
  const [volumeDiscounts, setVolumeDiscounts] = useState(() => [])
  const [productPrices, setProductPrices] = useState(() => [])
  const [priceTiers, setPriceTiers] = useState(() => [])
  const [loading, setLoading] = useState(() => false)
  const [editingItem, setEditingItem] = useState(() => null)
  const [showForm, setShowForm] = useState(() => false)
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || defaultTab || 'products')
  const [sortConfig, setSortConfig] = useState(() => ({ key: 'name', direction: 'asc' }))
  const [strainFamilies, setStrainFamilies] = useState(() => [])

  // Load data function - MOVED BEFORE useEffect to prevent TDZ
  const loadData = useCallback(async () => {
    setLoading(true)
    console.log('Loading data...')
    try {
      const [productsData, productTypesData, volumeDiscountsData, priceTiersData, productPricesData, familiesResult] = await Promise.all([
        fetchProducts(),
        fetchProductTypes(),
        fetchVolumeDiscounts(),
        fetchPriceTiers(),
        fetchProductPrices(),
        supabase.from('strain_families').select('id, name, code, display_order, active, product_family_id, product_families(id, name, reference_code)').eq('active', true).order('display_order')
      ])
      const familiesData = familiesResult.data || []
      console.log('Loaded data:', {
        products: productsData.length,
        productTypes: productTypesData.length,
        volumeDiscounts: volumeDiscountsData.length,
        priceTiers: priceTiersData.length,
        strainFamilies: familiesData.length
      })
      // Sort products by default (strain, ascending)
      const productsDataCopy = [...productsData]
      const sortedProducts = productsDataCopy.sort((a, b) => {
        const aStr = (a.strain || '').toString()
        const bStr = (b.strain || '').toString()
        const aVal = aStr.toLowerCase()
        const bVal = bStr.toLowerCase()
        if (aVal < bVal) return -1
        if (aVal > bVal) return 1
        return 0
      })
      
      setProducts(sortedProducts)
      setProductTypes(productTypesData)
      setVolumeDiscounts(volumeDiscountsData)
      setPriceTiers(priceTiersData)
      setProductPrices(productPricesData)
      setStrainFamilies(familiesData)
    } catch (error) {
      console.error('Error loading data:', error)
      alert('Error loading data: ' + error.message)
    } finally {
      setLoading(false)
    }
  }, [])

  // Load products data
  useEffect(() => {
    loadData()
  }, [loadData])

  // Helper function to transform raw data
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕'
    return sortConfig.direction === 'asc' ? '↑' : '↓'
  }

  const handleBackToDashboard = () => {
    navigate('/')
  }

  const handleSort = (key) => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
    const productsCopy = [...products]
    const sortedProducts = productsCopy.sort((a, b) => {
      if (key === 'base_price_per_kg') {
        const aVal = a[key] || 0
        const bVal = b[key] || 0
        return direction === 'asc' ? aVal - bVal : bVal - aVal
      }
      if (key === 'active') {
        return direction === 'asc' 
          ? (a[key] === b[key] ? 0 : a[key] ? -1 : 1)
          : (a[key] === b[key] ? 0 : a[key] ? 1 : -1)
      }
      const aStr = (a[key] || '').toString()
      const bStr = (b[key] || '').toString()
      const aVal = aStr.toLowerCase()
      const bVal = bStr.toLowerCase()
      if (aVal < bVal) return direction === 'asc' ? -1 : 1
      if (aVal > bVal) return direction === 'asc' ? 1 : -1
      return 0
    })
    setProducts(sortedProducts)
  }

  const handleSave = async (data, type) => {
    console.log('=== HANDLE SAVE DEBUG ===')
    console.log('Data received:', data)
    console.log('Type received:', type)
    console.log('EditingItem:', editingItem)
    console.log('EditingItem.type:', editingItem?.type)
    console.log('========================')
    
    try {
      if (editingItem && editingItem.id) {
        console.log('Updating existing item:', editingItem.id)
        // Update existing item
        switch (type) {
          case 'product':
            console.log('UPDATING PRODUCT with data:', data)
            await updateProduct(editingItem.id, data)
            break
          case 'productType':
            console.log('UPDATING PRODUCT TYPE with data:', data)
            await updateProductType(editingItem.id, data)
            break
          case 'volumeDiscount':
            console.log('UPDATING VOLUME DISCOUNT with data:', data)
            await updateVolumeDiscount(editingItem.id, data)
            break
        }
      } else {
        console.log('Creating new item of type:', type)
        // Create new item
        switch (type) {
          case 'product': {
            console.log('CREATING PRODUCT with data:', data)
            const result = await createProduct(data)
            console.log('createProduct result:', result)
            break
          }
          case 'productType': {
            console.log('CREATING PRODUCT TYPE with data:', data)
            const ptResult = await createProductType(data)
            console.log('createProductType result:', ptResult)
            break
          }
          case 'volumeDiscount': {
            console.log('CREATING VOLUME DISCOUNT with data:', data)
            const vdResult = await createVolumeDiscount(data)
            console.log('createVolumeDiscount result:', vdResult)
            break
          }
          default:
            console.error('Unknown type:', type)
            return
        }
      }
      
      console.log('Reloading data...')
      await loadData()
      if (type === 'product') invalidateCache('products')
      if (type === 'productType') invalidateCache('productTypes')
      if (type === 'volumeDiscount') invalidateCache('volumeDiscounts')
      setShowForm(false)
      setEditingItem(null)
      console.log('Save completed successfully')
    } catch (error) {
      console.error('Error saving:', error)
      alert('Error saving: ' + error.message)
    }
  }

  const handleDelete = async (id, type) => {
    if (!confirm('Are you sure you want to delete this item?')) return
    
    try {
      switch (type) {
        case 'product':
          await deleteProduct(id)
          break
        case 'productType':
          await deleteProductType(id)
          break
        case 'volumeDiscount':
          await deleteVolumeDiscount(id)
          break
      }
      await loadData()
      if (type === 'product') invalidateCache('products')
      if (type === 'productType') invalidateCache('productTypes')
      if (type === 'volumeDiscount') invalidateCache('volumeDiscounts')
    } catch (error) {
      console.error('Error deleting:', error)
    }
  }

  const handleEdit = (item, type) => {
    // Split object spread to prevent TDZ
    const editItem = {}
    Object.assign(editItem, item)
    editItem.type = type
    setEditingItem(editItem)
    setShowForm(true)
  }

  const handleAddNew = (type) => {
    // Split complex object creation to prevent TDZ
    let newItem = {}
    newItem.type = type
    
    // Initialize with default values based on type
    if (type === 'product') {
      newItem.type = type
      newItem.strain = ''
      newItem.strain_family_id = ''
      newItem.sku = ''
      newItem.base_price_per_kg = 0
      newItem.active = true
    } else if (type === 'productType') {
      newItem.type = type
      newItem.name = ''
      newItem.price_modifier_percent = 0
      newItem.active = true
    } else if (type === 'volumeDiscount') {
      // Find wholesale tier as default (volume discounts only apply to wholesale)
      const wholesaleTier = priceTiers.find(t => t.name === 'wholesale')
      newItem.type = type
      newItem.price_tier_id = wholesaleTier?.id || ''
      newItem.product_id = ''
      newItem.min_quantity = 0
      newItem.discount_percent = 0
      newItem.active = true
    }
    
    console.log('handleAddNew setting editingItem:', newItem)
    setEditingItem(newItem)
    setShowForm(true)
  }

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  return (
    <PageLayout
      title="Products & Settings"
      user={user}
      onLogout={onLogout}
    >
      <div className="h-full overflow-y-auto">
        <div className="sales-form-container">
          <div className="table-header">
            <div className="table-header-actions">
              <DashboardTabs
                tabs={[
                  { value: 'products', label: 'Products & Strains', icon: <Package className="h-4 w-4" /> },
                  { value: 'families', label: 'Strain Families', icon: <Layers className="h-4 w-4" /> },
                  { value: 'types', label: 'Product Types', icon: <Settings className="h-4 w-4" /> },
                  { value: 'pricing', label: 'Pricing Tiers', icon: <Coins className="h-4 w-4" /> },
                  { value: 'catalogue', label: 'Price Catalogue', icon: <BookOpen className="h-4 w-4" /> },
                  { value: 'thresholds', label: 'Wholesale Thresholds', icon: <Scale className="h-4 w-4" /> },
                  { value: 'volume', label: 'Volume Discounts', icon: <Percent className="h-4 w-4" /> },
                  { value: 'promotions', label: 'Promotions', icon: <Tag className="h-4 w-4" /> },
                  { value: 'blockFormats', label: 'Block Formats', icon: <Box className="h-4 w-4" /> },
                  { value: 'blockPrices', label: 'Block Prices', icon: <Box className="h-4 w-4" /> },
                  { value: 'growingMaterials', label: 'Growing Materials', icon: <Sprout className="h-4 w-4" /> },
                  { value: 'growingMaterialPrices', label: 'Growing Material Prices', icon: <Sprout className="h-4 w-4" /> }
                ]}
                value={activeTab}
                onValueChange={setActiveTab}
                size="regular"
              />
              {activeTab === 'types' && (
                <Button variant="create" onClick={() => handleAddNew('productType')}>
                  + Add New Type
                </Button>
              )}
              {activeTab === 'volume' && (
                <Button variant="create" onClick={() => handleAddNew('volumeDiscount')}>
                  + Add Volume Discount
                </Button>
              )}
            </div>
          </div>

          {activeTab === 'products' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5 text-[#009689]" />
                      Products & Strains
                    </CardTitle>
                    <CardDescription>Manage your mushroom strains and their base prices</CardDescription>
                  </div>
                  <Button variant="create" onClick={() => handleAddNew('product')}>
                    + Add New Product
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead onClick={() => handleSort('sku')} className="cursor-pointer hover:bg-muted/50">
                          SKU {getSortIcon('sku')}
                        </TableHead>
                        <TableHead onClick={() => handleSort('strain')} className="cursor-pointer hover:bg-muted/50">
                          Strain {getSortIcon('strain')}
                        </TableHead>
                        <TableHead onClick={() => handleSort('base_price_per_kg')} className="cursor-pointer hover:bg-muted/50">
                          Base Price (£/kg) {getSortIcon('base_price_per_kg')}
                        </TableHead>
                        <TableHead onClick={() => handleSort('active')} className="cursor-pointer hover:bg-muted/50">
                          Status {getSortIcon('active')}
                        </TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map(product => {
                        const family = strainFamilies.find(f => f.id === product.strain_family_id)
                        return (
                        <TableRow key={product.id}>
                          <TableCell>{product.sku}</TableCell>
                          <TableCell>{family ? `${family.name} (${family.code})` : product.strain}</TableCell>
                          <TableCell>£{product.base_price_per_kg?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              product.active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {product.active ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <OdinButton variant="table-action" size="small" leftIcon={<EditIcon />} onClick={() => handleEdit(product, 'product')}>Edit</OdinButton>
                          </TableCell>
                        </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'families' && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Layers className="h-5 w-5 text-[#009689]" />
                      Strain Families & Product Families
                    </CardTitle>
                    <CardDescription>Each strain family belongs to a product family whose reference code appears on invoices</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Strain Family</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Product Family</TableHead>
                        <TableHead>Invoice Ref</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {strainFamilies.map(family => (
                        <TableRow key={family.id}>
                          <TableCell className="font-medium">{family.name}</TableCell>
                          <TableCell>{family.code}</TableCell>
                          <TableCell>{family.product_families?.name || 'Unassigned'}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {family.product_families?.reference_code || '—'}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'types' && (
            <ProductTypesTab
              productTypes={productTypes}
              onEdit={(item) => handleEdit(item, 'productType')}
              onDelete={(id) => handleDelete(id, 'productType')}
              onAddNew={() => handleAddNew('productType')}
            />
          )}

          {activeTab === 'pricing' && (
            <PricingSettings />
          )}

          {activeTab === 'catalogue' && (
            <PriceCatalogue />
          )}

          {activeTab === 'thresholds' && (
            <WholesaleThresholds />
          )}

          {activeTab === 'volume' && (
            <VolumeDiscountsTab
              volumeDiscounts={volumeDiscounts}
              products={products}
              priceTiers={priceTiers}
              productPrices={productPrices}
              onEdit={(item) => handleEdit(item, 'volumeDiscount')}
              onDelete={(id) => handleDelete(id, 'volumeDiscount')}
              onAddNew={() => handleAddNew('volumeDiscount')}
            />
          )}

          {activeTab === 'promotions' && (
            <PromotionsTab />
          )}

          {activeTab === 'blockFormats' && (
            <BlockFormatsTab />
          )}

          {activeTab === 'blockPrices' && (
            <BlockPriceCatalogue />
          )}

          {activeTab === 'growingMaterials' && (
            <GrowingMaterialsTab />
          )}

          {activeTab === 'growingMaterialPrices' && (
            <GrowingMaterialPriceCatalogue />
          )}
        </div>
      </div>

        <Dialog open={showForm} onOpenChange={(open) => {
          setShowForm(open)
          if (!open) {
            setEditingItem(null)
          }
        }}>
          <ProductForm
            key={editingItem?.id || `new-${editingItem?.type || 'none'}`}
            item={editingItem}
            products={products}
            productTypes={productTypes}
            priceTiers={priceTiers}
            productPrices={productPrices}
            strainFamilies={strainFamilies}
            onSave={handleSave}
            onDelete={handleDelete}
            onClose={() => {
              setShowForm(false)
              setEditingItem(null)
            }}
            onDataChange={loadData}
          />
        </Dialog>
    </PageLayout>
  )
}


// Product Types Tab Component
function ProductTypesTab({ productTypes, onEdit, onDelete, onAddNew }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-[#009689]" />
              Product Types
            </CardTitle>
            <CardDescription>Configure pricing modifiers for different product classifications</CardDescription>
          </div>
          <Button onClick={onAddNew}>
            Add New Type
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Price Modifier</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productTypes.map(type => {
                const multiplier = parseFloat(type.price_multiplier) || 1.0
                let displayText = ''
                if (multiplier === 0) {
                  displayText = 'Free (0×)'
                } else if (multiplier === 1.0) {
                  displayText = 'Base price (1.00×)'
                } else if (multiplier > 1.0) {
                  const premium = Math.round((multiplier - 1) * 100)
                  displayText = `+${premium}% premium (${multiplier.toFixed(2)}×)`
                } else {
                  const discount = Math.round((1 - multiplier) * 100)
                  displayText = `${discount}% discount (${multiplier.toFixed(2)}×)`
                }

                return (
                  <TableRow key={type.id}>
                    <TableCell>{type.name}</TableCell>
                    <TableCell>{displayText}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        type.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {type.active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <OdinButton variant="table-action" size="small" leftIcon={<EditIcon />} onClick={() => onEdit(type)}>Edit</OdinButton>
                        <Button variant="destructive" size="sm" onClick={() => onDelete(type.id)}>Delete</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

// Volume Discounts Tab Component
function VolumeDiscountsTab({ volumeDiscounts, products, priceTiers, productPrices = [], onEdit, onDelete, onAddNew }) {
  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId)
    return product ? product.strain : 'Unknown Product'
  }

  // Get the catalogue price for a product × tier (using highest-multiplier grade, typically A Class)
  const getCataloguePrice = (productId, priceTierId) => {
    const matching = productPrices.filter(p =>
      p.product_id === productId &&
      p.price_tier_id === priceTierId &&
      p.product_type?.active
    )
    if (matching.length === 0) return null
    // Use the standard grade (multiplier closest to 1.0) as the reference price.
    // Volume discounts apply to the product as a whole, so the canonical "1x" grade
    // is the meaningful baseline for the displayed discount %.
    matching.sort((a, b) => {
      const aDist = Math.abs((a.product_type?.price_multiplier || 1) - 1)
      const bDist = Math.abs((b.product_type?.price_multiplier || 1) - 1)
      return aDist - bDist
    })
    return parseFloat(matching[0].price_per_kg)
  }

  const getEffectivePrice = (discount) => {
    // Prefer the absolute selling price stored on the row.
    if (discount.price_per_kg != null && discount.price_per_kg !== '') {
      return Number(discount.price_per_kg)
    }
    // Fallback for rows pre-dating the price_per_kg column.
    const cataloguePrice = getCataloguePrice(discount.product_id, discount.price_tier_id)
    if (cataloguePrice) {
      return cataloguePrice * (1 - discount.discount_percent / 100)
    }
    const product = products.find(p => p.id === discount.product_id)
    if (!product?.base_price_per_kg || !discount.price_tier?.multiplier) return null
    const tierPrice = product.base_price_per_kg * discount.price_tier.multiplier
    return tierPrice * (1 - discount.discount_percent / 100)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-[#6366F1]" />
              Volume Discounts
            </CardTitle>
            <CardDescription>Set up quantity-based pricing incentives by pricing tier (wholesale only)</CardDescription>
          </div>
          <Button onClick={onAddNew}>
            Add Volume Discount
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pricing Tier</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Min Quantity (kg)</TableHead>
                <TableHead>Selling Price (£/kg)</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {volumeDiscounts.map(discount => (
                <TableRow key={discount.id}>
                  <TableCell>{discount.price_tier?.display_name || 'Unknown'}</TableCell>
                  <TableCell>{discount.product?.strain || getProductName(discount.product_id)}</TableCell>
                  <TableCell>{discount.min_quantity}kg</TableCell>
                  <TableCell className="font-medium">
                    {(() => {
                      const price = getEffectivePrice(discount)
                      if (price === null) return '—'
                      const baseline = getCataloguePrice(discount.product_id, discount.price_tier_id)
                      const showStrike = baseline != null && baseline - price > 0.005
                      return (
                        <span className="inline-flex items-center gap-2">
                          {showStrike && (
                            <span className="text-muted-foreground line-through font-normal">
                              £{baseline.toFixed(2)}
                            </span>
                          )}
                          <span>£{price.toFixed(2)}</span>
                        </span>
                      )
                    })()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{discount.discount_percent}%</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      discount.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {discount.active ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <OdinButton variant="table-action" size="small" leftIcon={<EditIcon />} onClick={() => onEdit(discount)}>Edit</OdinButton>
                      <Button variant="destructive" size="sm" onClick={() => onDelete(discount.id)}>Delete</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

// Universal Form Component
function ProductForm({ item, products, productTypes, priceTiers, productPrices = [], strainFamilies = [], onSave, onDelete, onClose, onDataChange }) {

  // Get the catalogue price for a product × tier, using the standard grade
  // (multiplier closest to 1.0) as the reference. See VolumeDiscountsTab.getCataloguePrice.
  const getCataloguePrice = (productId, priceTierId) => {
    if (!productId || !priceTierId) return null
    const matching = productPrices.filter(p =>
      p.product_id === productId &&
      p.price_tier_id === priceTierId &&
      p.product_type?.active
    )
    if (matching.length === 0) return null
    matching.sort((a, b) => {
      const aDist = Math.abs((a.product_type?.price_multiplier || 1) - 1)
      const bDist = Math.abs((b.product_type?.price_multiplier || 1) - 1)
      return aDist - bDist
    })
    return parseFloat(matching[0].price_per_kg)
  }

  // Initialize form data based on item type
  const initializeFormData = (item) => {
    const baseData = {
      active: item?.active !== undefined ? item.active : true
    }

    if (item?.type === 'product') {
      return {
        ...baseData,
        strain: item?.strain || '',
        strain_family_id: item?.strain_family_id || '',
        sku: item?.sku || '',
        base_price_per_kg: item?.base_price_per_kg || ''
      }
    } else if (item?.type === 'productType') {
      return {
        ...baseData,
        name: item?.name || '',
        price_multiplier: item?.price_multiplier || ''
      }
    } else if (item?.type === 'volumeDiscount') {
      // Find wholesale tier as default (volume discounts only apply to wholesale)
      const wholesaleTier = priceTiers?.find(t => t.name === 'wholesale')
      const tierId = item?.price_tier_id || wholesaleTier?.id || ''
      const productId = item?.product_id || ''

      // Prefer the stored absolute selling price; fall back to deriving it from discount_percent
      // for rows saved before price_per_kg was introduced.
      let sellingPrice = ''
      if (item?.price_per_kg != null && item.price_per_kg !== '') {
        sellingPrice = Number(item.price_per_kg).toFixed(2)
      } else if (item?.discount_percent && productId && tierId) {
        const catPrice = getCataloguePrice(productId, tierId)
        if (catPrice) {
          sellingPrice = (catPrice * (1 - item.discount_percent / 100)).toFixed(2)
        }
      }

      return {
        ...baseData,
        price_tier_id: tierId,
        product_id: productId,
        min_quantity: item?.min_quantity || '',
        selling_price: sellingPrice,
        discount_percent: item?.discount_percent || ''
      }
    }

    return baseData
  }

  const [formData, setFormData] = useState(initializeFormData(item))

  // Update form data when item changes
  useEffect(() => {
    setFormData(initializeFormData(item))
  }, [item])

  const handleSubmit = (e) => {
    e.preventDefault()
    console.log('Form submitted:', formData, 'Type:', item?.type)

    // Submit form data directly without type field mapping
    const submitData = { ...formData }

    // For volume discounts, store the absolute selling price; derive discount_percent
    // for back-compat with resolve_price() which still reads the percent.
    if (item?.type === 'volumeDiscount') {
      const sellingPrice = parseFloat(submitData.selling_price)
      submitData.price_per_kg = Number.isFinite(sellingPrice) ? sellingPrice : null
      const catPrice = getCataloguePrice(submitData.product_id, submitData.price_tier_id)
      if (catPrice && Number.isFinite(sellingPrice) && sellingPrice >= 0) {
        submitData.discount_percent = Math.round(((1 - sellingPrice / catPrice) * 100) * 100) / 100
      } else {
        submitData.discount_percent = 0
      }
      delete submitData.selling_price
    }

    onSave(submitData, item?.type)
  }

  const handleDelete = () => {
    if (item?.id && confirm('Are you sure you want to delete this item?')) {
      onDelete(item.id, item.type)
      onClose()
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  // Don't render if item is null
  if (!item) {
    return null
  }

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>
          {item.id ? 'Edit' : 'Add New'} {' '}
          {item.type === 'product' && 'Product'}
          {item.type === 'productType' && 'Product Type'}
          {item.type === 'volumeDiscount' && 'Volume Discount'}
        </DialogTitle>
      </DialogHeader>

      <form id="product-form" onSubmit={handleSubmit} className="space-y-4">
          {item.type === 'product' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Strain Family</label>
                <StrainFamilyComboBox
                  value={formData.strain_family_id || null}
                  onChange={(familyId, family) => {
                    handleChange('strain_family_id', familyId || '')
                    if (family) handleChange('strain', family.name)
                  }}
                  onDataChange={onDataChange}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">SKU</label>
                <Input
                  value={formData.sku || ''}
                  onChange={(e) => handleChange('sku', e.target.value)}
                  placeholder="Stock Keeping Unit"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Base Price £/kg</label>
                <DecimalInput
                  value={formData.base_price_per_kg || ''}
                  onChange={(value) => handleChange('base_price_per_kg', value)}
                  decimalPlaces={2}
                  min={0}
                  autoClearValue="0.00"
                  placeholder="0.00"
                  required
                />
              </div>
            </>
          )}

          {item.type === 'productType' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Type Name</label>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Price Multiplier
                  <span className="text-xs text-gray-500 block mt-1">
                    Examples: 1.60 (60% premium), 1.00 (base), 0.75 (25% discount), 0.00 (free)
                  </span>
                </label>
                <DecimalInput
                  value={formData.price_multiplier || ''}
                  onChange={(value) => handleChange('price_multiplier', value)}
                  decimalPlaces={2}
                  min={0}
                  autoClearValue="1.00"
                  placeholder="1.00"
                  required
                />
              </div>
            </>
          )}

          {item.type === 'volumeDiscount' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Pricing Tier</label>
                <Select
                  value={formData.price_tier_id || ''}
                  onValueChange={(value) => { if (value) handleChange('price_tier_id', value) }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select pricing tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {priceTiers.map(tier => (
                      <SelectItem key={tier.id} value={tier.id}>{tier.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Product</label>
                <Select
                  value={formData.product_id || ''}
                  onValueChange={(value) => { if (value) handleChange('product_id', value) }}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id}>{product.strain}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Minimum Quantity (kg)</label>
                <DecimalInput
                  value={formData.min_quantity || ''}
                  onChange={(value) => handleChange('min_quantity', value)}
                  decimalPlaces={2}
                  min={0}
                  autoClearValue="0.00"
                  placeholder="0.00"
                  required
                />
              </div>
              {(() => {
                const catPrice = getCataloguePrice(formData.product_id, formData.price_tier_id)
                const sellingPrice = parseFloat(formData.selling_price)
                const discountPct = catPrice && sellingPrice >= 0
                  ? Math.round(((1 - sellingPrice / catPrice) * 100) * 100) / 100
                  : null
                return (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Selling Price £/kg
                      {catPrice && (
                        <span className="text-xs text-gray-500 block mt-1">
                          Catalogue price: £{catPrice.toFixed(2)}
                        </span>
                      )}
                    </label>
                    <DecimalInput
                      value={formData.selling_price || ''}
                      onChange={(value) => handleChange('selling_price', value)}
                      decimalPlaces={2}
                      min={0}
                      autoClearValue="0.00"
                      placeholder="0.00"
                      required
                    />
                    {discountPct !== null && discountPct > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {discountPct.toFixed(1)}% discount from catalogue price
                      </p>
                    )}
                    {discountPct !== null && discountPct <= 0 && sellingPrice > 0 && (
                      <p className="text-xs text-amber-600">
                        Selling price is at or above catalogue price — no discount applied
                      </p>
                    )}
                  </div>
                )
              })()}
            </>
          )}

          <div className="flex items-center justify-between py-2">
            <label className="text-sm font-medium text-gray-700">
              {formData.active ? 'Active' : 'Inactive'}
            </label>
            <ToggleSwitch 
              checked={formData.active}
              onCheckedChange={(checked) => handleChange('active', checked)}
            />
          </div>

        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          {item?.id && (
            <Button type="button" variant="destructive" onClick={handleDelete}>Delete</Button>
          )}
          <Button
            type="submit"
            form="product-form"
            variant={item?.id ? "default" : "create"}
          >
            {item?.id ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
    </DialogContent>
  )
}

export default ProductDashboard
